import React, { useEffect, useRef, useState } from 'react';
import { Icon } from 'scorer-ui-kit';
import { useTranslation } from 'react-i18next';
import { ColorsTranslate } from '../constants';
import styled from 'styled-components';
interface PropsTest {
  isActive: boolean;
}

const SelectColorParent = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  font-family: Lato;
  font-size: 16px;
  border: 1px solid rgb(217, 218, 217);
  box-sizing: border-box;
  outline: none;
  background: #fff;
  color: #808080;
  z-index: 2;
`;

const SelectedItemsContainer = styled.div`
  padding: 0 5px 0 10px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SelectedItems = styled.div`
  max-width: 200px;
  height: 35px;
  overflow-y: auto;
  display: flex;
  align-items: center;
  z-index: 1;
  scrollbar-width: thin;
  scrollbar-color: #888 #fff;
  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  /* Track */
  &::-webkit-scrollbar-track {
    background: none;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;
const SelectedItem = styled.p`
  text-transform: capitalize;
  margin: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0 5px;
  padding: 2px 8px;
  border: 1px solid rgb(217, 218, 217);
  border-radius: 5px;
  cursor: pointer;
`;
const CrossIconContainer = styled.div`
  & > div {
    display: flex;
  }
`;
const DropMenuContainer = styled.div`
  padding: 5px 10px;
  display: flex;
  gap: 8px 0;
  flex-direction: column;
  border: 1px solid #646363;
  box-sizing: border-box;
  outline: none;
  background: #fff;
  visibility: hidden;
  &:hover {
    visibility: visible;
  }
`;
const ShowMenuInput = styled.input`
  appearance: none;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  outline: none;
  border: none;
  background: transparent none;
  cursor: default;
  &:focus + ${DropMenuContainer} {
    visibility: visible;
  }
`;
const DropMenuItems = styled.div`
  height: 20px;
  display: flex;
  gap: 0 8px;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  text-transform: capitalize;
  position: relative;
  cursor: pointer;
  &:before {
    content: "";
    appearance: none;
    background-color: transparent;
    border: 2px solid rgb(217, 218, 217);
    border-radius: 5px;
    padding: 7.5px;
    display: inline-block;
    position: relative;
    vertical-align: middle;
    cursor: pointer;
    margin-right: 10px;
  }
`;
const CheckboxInput = styled.input`
  display: none;
  &:checked + ${CheckboxLabel} {
    color: #42b0f5;
  }
  &:checked + ${CheckboxLabel}:before {
    border-color: #42b0f5;
  }
  &:checked + ${CheckboxLabel}:after {
    content: "";
    display: block;
    position: absolute;
    top: 4px;
    left: 6px;
    width: 4.5px;
    height: 9.5px;
    border: solid #42b0f5;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;
const OtherCheckBoxLabel = styled(CheckboxLabel)<PropsTest>`
  ${({ isActive }) =>
    isActive &&
    `
        color: rgb(243 243 243);
        cursor: not-allowed;
        user-select: none;
        &:before{
            border-color: rgb(243 243 243);
            cursor: not-allowed;
        }
    `}
`;

interface IMenuItems {
  text: string,
  range: string,
  isChecked: boolean
}

interface Props {
  onChangeColor(colorsData: string[]): void,
  desiredColors: string[],
  menuList: IMenuItems[]
}

const SelectColor = ({onChangeColor,desiredColors, menuList}: Props) => {
  // states
  const [selectItems, setSelectItems] = useState<IMenuItems[]>(menuList);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isDisable, setIsDisable] = useState<boolean>(false);
  const [focusActive, setFocusActive] = useState<boolean>(false);
  // refs
  const menuShowRef = useRef<HTMLInputElement>(null);// for hiding and revealing dropdown menu
  const noneRef = useRef<HTMLInputElement>(null);// for "None" checkbox
  const flagRef = useRef<string[]|null>(desiredColors); // flag for checking if initialization is finished
  // hooks
  const { t } = useTranslation(['Common']);

  // handles disable of all checkbox if none is selected
  const handleDisable = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsDisable(true);
      setSelectItems((prevVal) => {
        return prevVal.map((item) => {
          return { ...item, isChecked: false };
        });
      });
      setSelectedItems([e.target.value]);
    } else {
      setIsDisable(false);
      setSelectedItems([]);
    }
  };

  // handles checkbox click
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    let event = e.target as HTMLInputElement;
    setSelectItems((prevVal) => {
      return prevVal.map((item) => {
        if (item.text === event.value) {
          return { ...item, isChecked: !item.isChecked };
        } else {
          return item;
        }
      });
    });
    if(event.checked && event.value === 'user_specified'){
      setTimeout(() => {
        menuShowRef.current?.blur();
      });
    }
    setSelectedItems((prevVal) => {
      if (event.checked) {
        if(prevVal.includes('Not Available')){
          let temp = prevVal.filter((itm) => itm !== 'Not Available');
          return [...temp, event.value];
        }
        return [...prevVal, event.value];
      } else {
        return prevVal.filter((itm) => itm !== event.value);
      }
    });
  };

  // deletes item from selectedItems
  const handleDelete = (deleteItem: string) => {
    setSelectedItems((prevVal) => {
      return prevVal.filter((item) => item !== deleteItem);
    });
    
    if(deleteItem === 'None' ){
      if(noneRef.current){
        noneRef.current.checked = false;
        setIsDisable(false);
      }
    }
    setSelectItems((prevVal) => {
      return prevVal.map((item) => {
        if (item.text === deleteItem) {
          return { ...item, isChecked: false };
        } else {
          return item;
        }
      });
    });
  };

  // initializing selected items
  useEffect(() => {
    if(JSON.stringify(flagRef.current)===JSON.stringify(desiredColors)){
      desiredColors.forEach((desiredItem) => {
        if(desiredItem==='None'){
          if(noneRef.current){
            noneRef.current.click();
          }
        }
        
        setSelectItems((prevVal) => {
          return prevVal.map((item) => {
            if (item.text === desiredItem) {
              return { ...item, isChecked: true };
            } else {
              return item;
            }
          });
        });
      });
      setSelectedItems(desiredColors);
    }
    flagRef.current = null;
  }, [desiredColors]);

  // for changing desired_color in ROISettings
  useEffect(() => {
    onChangeColor(selectedItems);
  }, [selectedItems, onChangeColor]);

  // to toggle dropdown menu
  useEffect(()=>{
    if(focusActive){
      menuShowRef.current?.focus();
    } else {
      menuShowRef.current?.blur();
    }
  },[focusActive]);

  return (
    <SelectColorParent>
      <SelectedItemsContainer>
        <SelectedItems>
          <SelectedItem style={{ padding: '0', border: 'none', margin: '0' }} />
          {selectedItems.map((item,index) => (
            <SelectedItem style={index===0 ? { margin: '0 3px 0 0' } : {margin: '0 3px'}} key={`${item}${index}`}>
              {t('Common:color.' + ColorsTranslate[item])}
              <CrossIconContainer onClick={() => handleDelete(item)}>
                <Icon
                  color='dimmed'
                  icon='ClearField'
                  size={11}
                  weight='regular'
                />
              </CrossIconContainer>
            </SelectedItem>
          ))}
        </SelectedItems>
        <Icon color='dimmed' icon='Down' size={8} weight='regular' />
      </SelectedItemsContainer>
      <ShowMenuInput type='text' ref={menuShowRef} readOnly onClick={()=>setFocusActive(prevVal=>!prevVal)} onBlur={()=>setFocusActive(false)}/>
      <DropMenuContainer onClick={()=>menuShowRef.current?.focus()}>
        <DropMenuItems>
          <CheckboxInput
            type='checkbox'
            id='None'
            name='None'
            value='None'
            ref={noneRef}
            onChange={handleDisable}
          />
          <CheckboxLabel htmlFor='None'>{t('Common:color.' + ColorsTranslate['None'])}</CheckboxLabel>
        </DropMenuItems>
        {selectItems.map((item,index) => {
          return (
            <DropMenuItems key={`${index}${item.text}${JSON.stringify(item)}`}>
              <CheckboxInput
                type='checkbox'
                id={item.text}
                name={item.text}
                value={item.text}
                disabled={isDisable}
                checked={item.isChecked}
                onClick={handleClick}
              />
              <OtherCheckBoxLabel
                isActive={isDisable}
                htmlFor={item.text}
              >{t('Common:color.' + ColorsTranslate[item.text]) +' '+ item.range}
              </OtherCheckBoxLabel>
            </DropMenuItems>
          );
        })}
      </DropMenuContainer>
    </SelectColorParent>
  );
};

export default SelectColor;